import type { ProfileView, UpdateProfileRequest } from "@keydials/api-client";

/**
 * The decisions behind the personal Settings page, as a pure module.
 *
 * Same reasoning as `dialSettingsNav.ts` and web-admin's `crmNav.ts`: the interesting parts here are
 * judgements — has anything actually changed, is this person about to silence notifications they don't
 * realise are hanging off their phone number, what does an empty box mean — and web-user has no DOM test
 * harness, so testing them through a rendered page is not on the table.
 */

/** What the form currently holds. Both are raw input values, untrimmed. */
export interface ProfileDraft {
  displayName: string;
  phoneNumber: string;
}

/** The stored value an empty box means: blank CLEARS, it does not "leave alone". */
const cleared = (value: string): string | null => (value.trim() === "" ? null : value.trim());

/** The draft a freshly-loaded profile starts from. */
export const draftFrom = (profile: ProfileView): ProfileDraft => ({
  displayName: profile.displayName ?? "",
  phoneNumber: profile.phoneNumber ?? "",
});

/**
 * Whether Save should be offered. Compared on the TRIMMED values, so adding a trailing space to a name
 * doesn't arm a button that would then save nothing — the server trims too, and a Save that provably
 * changes nothing reads as broken the moment it leaves the field unchanged.
 */
export function isDirty(profile: ProfileView | null, draft: ProfileDraft): boolean {
  if (profile === null) {
    return false;
  }
  return cleared(draft.displayName) !== (profile.displayName ?? null)
    || cleared(draft.phoneNumber) !== (profile.phoneNumber ?? null);
}

/**
 * How many live SMS subscriptions this save is about to switch off, or 0 for none.
 *
 * Only when a number that EXISTS is being emptied — changing it to a different number keeps every
 * subscription, and someone who never had one has nothing to lose. The server does the clearing (a
 * subscription without a number shows as on and delivers nothing); this is what warns first.
 */
export function textsAboutToGoQuiet(profile: ProfileView | null, draft: ProfileDraft): number {
  if (profile === null || !profile.phoneNumber || draft.phoneNumber.trim() !== "") {
    return 0;
  }
  return profile.smsSubscriptionCount;
}

/** The request a draft becomes. */
export const toRequest = (draft: ProfileDraft): UpdateProfileRequest => ({
  displayName: cleared(draft.displayName),
  phoneNumber: cleared(draft.phoneNumber),
});
